/* =============================================================
   KPKL Radio — Home Page
   Design: "Smash & Stream" — Dark Sports Dashboard
   PRIMARY TARGET: Mobile (375px+), then tablet/desktop
   Stream: https://cast4.my-control-panel.com/proxy/wesellpickleball/stream
   Metadata: tRPC proxy to cast4 stats; browser fallback via allorigins if the server cannot reach cast4
   ============================================================= */

import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import { Link } from "wouter";
import CoverFlow, { type CoverFlowItem } from "@/components/CoverFlow";
import { SiteLogo } from "@/components/SiteLogo";
import { trpc } from "@/lib/trpc";
import { fetchStatsXmlViaCorsProxy } from "@/lib/fetchStatsViaProxy";
import { parseShoutcastStatsXml } from "@shared/radioStats";
import { SITE_DOMAIN, SITE_NAME, SITE_TAGLINE, SITE_URL } from "@shared/site";
import {
  CENTOVA_STATION_ACCOUNTS,
  PODCAST_EPISODES,
  podcastLoopPosition,
  statsEndpointsFor,
  streamEndpointsFor,
} from "@shared/streamEndpoints";
import { useKaliBroadcast, type StreamGraph } from "@/hooks/useKaliBroadcast";
import { useNowPlayingNotifications } from "@/hooks/useNowPlayingNotifications";
import { sponsorshipForStation } from "@shared/sponsors";

// ─── Types ─────────────────────────────────────────────────────────────────────
interface NowPlaying {
  artist: string;
  title: string;
  listeners?: number;
}

// ─── Station Data ──────────────────────────────────────────────────────────────
const STATION_DEFS = [
  {
    id: "eclectic",
    name: "The Pickle Jar",
    tagline: "Eclectic Pickleball Hits",
    description:
      "The original pickleball station — an eclectic mix of music handpicked for the court. From indie anthems to feel-good classics, all with a pickleball twist.",
    genre: "Eclectic",
    genreColor: "#7dc832",
    frequency: "PB 98.7",
    centovaUser: CENTOVA_STATION_ACCOUNTS["eclectic"],
    live: true,
    image: "/stations/pickle-jar.png",
  },
  {
    id: "classic-rock",
    name: "Rock the Court",
    tagline: "Classic Rock for Old-School Ballers",
    description:
      "For the seasoned player who grew up on Zeppelin, Springsteen, and Fleetwood Mac. Crank it up between games and feel the thunder.",
    genre: "Classic Rock",
    genreColor: "#f97316",
    frequency: "PB 104.3",
    centovaUser: CENTOVA_STATION_ACCOUNTS["classic-rock"],
    live: true,
    image: "/stations/rock-the-court.png",
  },
  {
    id: "country",
    name: "Dink & Twang",
    tagline: "Country Roads to the Kitchen",
    description:
      "Boot-scootin' beats for outdoor courts and tailgate tournaments. Country classics and modern hits for the pickleball faithful.",
    genre: "Country",
    genreColor: "#f59e0b",
    frequency: "PB 91.5",
    centovaUser: CENTOVA_STATION_ACCOUNTS["country"],
    live: true,
    image: "/stations/dink-and-twang.png",
  },
  {
    id: "hiphop",
    name: "Dink Don't Stop",
    tagline: "Hip-Hop & Urban Beats",
    description:
      "Street-level energy meets court-side swagger. Hip-hop, R&B, and trap bangers to keep your footwork sharp and your serve harder.",
    genre: "Hip-Hop / R&B",
    genreColor: "#a855f7",
    frequency: "PB 107.1",
    centovaUser: CENTOVA_STATION_ACCOUNTS["hiphop"],
    live: true,
    image: "/stations/dink-dont-stop.png",
  },
  {
    id: "pop",
    name: "Pop & Dink",
    tagline: "Pop Hits & Indie Vibes",
    description:
      "Chart-toppers, indie gems, and feel-good anthems for the social player. Perfect for mixed doubles and post-game hangouts.",
    genre: "Pop / Indie",
    genreColor: "#ec4899",
    frequency: "PB 95.3",
    centovaUser: CENTOVA_STATION_ACCOUNTS["pop"],
    live: true,
    image: "/stations/pop-and-dink.png",
  },
  {
    id: "edm",
    name: "Smash & Drop",
    tagline: "Electronic & EDM Drops",
    description:
      "High-BPM electronic music engineered for peak performance. Drop the beat, drop the third shot, drop the dink — this station hits as hard as your overhead smash.",
    genre: "Electronic / EDM",
    genreColor: "#00d4ff",
    frequency: "PB 102.9",
    centovaUser: CENTOVA_STATION_ACCOUNTS["edm"],
    live: true,
    image: "/stations/smash-and-drop.png",
  },
  {
    id: "christian",
    name: "Grace & Dink",
    tagline: "Inspirational Music for the Court",
    description:
      "Faith-filled music for the pickleball faithful. Contemporary Christian hits, worship anthems, and uplifting songs to keep your spirit high and your game inspired.",
    genre: "Christian",
    genreColor: "#f0c040",
    frequency: "PB 88.9",
    centovaUser: CENTOVA_STATION_ACCOUNTS["christian"],
    live: true,
    image: "/stations/grace-and-dink.png",
  },
  {
    id: "wind-down",
    name: "Wind Down",
    tagline: "Meditation, Relaxation & Hypnotic Sounds",
    description:
      "Unwind after the match with calming nature sounds, guided meditations, and hypnotic rhythms. The perfect sonic cool-down for mind and body.",
    genre: "Meditation / Hypnotic",
    genreColor: "#38bdf8",
    frequency: "PB 87.5",
    centovaUser: CENTOVA_STATION_ACCOUNTS["wind-down"],
    live: true,
    image: "/stations/wind-down.png",
  },
];

/**
 * Station = def + ordered failover endpoint chains derived from the Centova
 * account name (issue #52). Primary is the cast4 proxy URL; fallbacks are
 * the same proxy path on the provider's sibling hosts — see
 * shared/streamEndpoints.ts.
 *
 * The Pickldiction Podcast channel (issue #106) is appended as its own
 * station entry AFTER the 8 music stations: it loops the podcast catalog
 * (episode MP3s on picklediction.com) instead of a Centova stream, and the
 * 24/7 join-in-progress offset comes from podcastLoopPosition(). It is
 * never mixed into any music station's rotation.
 */
const STATIONS = [
  ...STATION_DEFS.map(def => {
    const streamUrls = streamEndpointsFor(def.centovaUser);
    const statsUrls = statsEndpointsFor(def.centovaUser);
    return {
      ...def,
      kind: "music" as const,
      streamUrl: streamUrls[0],
      streamUrls,
      statsUrl: statsUrls[0] as string | null,
      statsUrls,
    };
  }),
  {
    id: "podcast",
    name: "The Pickldiction Podcast",
    tagline: "One Word. One Episode. 24/7.",
    description:
      "The Pickldiction Podcast with DJAI kali + Master Hypnotist Dominick DeCarlo — one word, one episode. The full catalog loops around the clock; tune in any time and join mid-episode.",
    genre: "Podcast",
    genreColor: "#c084fc",
    frequency: "PB 90.1",
    centovaUser: undefined as never,
    live: true,
    image: "/stations/pickle-podcast.png",
    kind: "podcast" as const,
    streamUrl: PODCAST_EPISODES[podcastLoopPosition().episodeIndex].audio,
    streamUrls: PODCAST_EPISODES.map(ep => ep.audio),
    statsUrl: null as string | null,
    statsUrls: [] as string[],
  },
];

// ─── Waveform Component ────────────────────────────────────────────────────────
function Waveform({
  playing,
  small = false,
}: {
  playing: boolean;
  small?: boolean;
}) {
  const bars = small ? 6 : 10;
  return (
    <div
      className="flex items-end gap-[2px]"
      style={{ height: small ? 16 : 28 }}
    >
      {Array.from({ length: bars }).map((_, i) => (
        <div
          key={i}
          className={`waveform-bar${playing ? "" : " paused"}`}
          style={{ animationDelay: `${i * 0.08}s` }}
        />
      ))}
    </div>
  );
}

// ─── Live Badge ────────────────────────────────────────────────────────────────
function LiveBadge() {
  return (
    <span className="flex items-center gap-1.5 bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider">
      <span className="live-dot" />
      LIVE
    </span>
  );
}

// ─── Station Card ──────────────────────────────────────────────────────────────
function StationCard({
  station,
  isActive,
  isPlaying,
  nowPlaying,
  onSelect,
}: {
  station: (typeof STATIONS)[0];
  isActive: boolean;
  isPlaying: boolean;
  nowPlaying: NowPlaying | null;
  onSelect: (station: (typeof STATIONS)[0]) => void;
}) {
  const handleClick = () => {
    onSelect(station);
  };

  return (
    <div
      className={`station-card rounded-xl overflow-hidden bg-card cursor-pointer ${isActive ? "active" : ""}`}
      onClick={handleClick}
      style={{ minWidth: 240 }}
    >
      {/* Card image */}
      <div className="relative overflow-hidden" style={{ aspectRatio: "4/3" }}>
        <img
          src={station.image}
          alt={station.name}
          className="w-full h-full object-cover transition-transform duration-500"
          style={{ filter: "none" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Top badges */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-2">
          {station.live && <LiveBadge />}
        </div>

        {/* Frequency tag */}
        <div className="absolute top-2.5 right-2.5">
          <span className="text-xs text-white/70 font-mono bg-black/50 px-2 py-0.5 rounded">
            {station.frequency}
          </span>
        </div>

        {/* Active playing indicator */}
        {isActive && (
          <div className="absolute bottom-2.5 right-2.5">
            <Waveform playing={isPlaying} small />
          </div>
        )}
      </div>

      {/* Card body */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-0.5">
          <h3
            className="font-bold text-base leading-tight"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              letterSpacing: "0.02em",
            }}
          >
            {station.name}
          </h3>
          <span
            className="text-xs font-semibold px-1.5 py-0.5 rounded-full shrink-0 mt-0.5"
            style={{
              color: station.genreColor,
              background: `${station.genreColor}18`,
            }}
          >
            {station.genre}
          </span>
        </div>

        <p className="text-xs text-muted-foreground mb-1.5 font-medium">
          {station.tagline}
        </p>

        {/* Now playing or coming soon */}
        {station.live && nowPlaying ? (
          <div className="pt-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">
              Now Playing
            </p>
            <p className="text-sm font-semibold text-foreground truncate">
              {nowPlaying.title}
            </p>
            {nowPlaying.artist && (
              <p className="text-xs text-muted-foreground truncate">
                {nowPlaying.artist}
              </p>
            )}
          </div>
        ) : null}

        {/* Listeners */}
        {station.live &&
          nowPlaying?.listeners != null &&
          nowPlaying.listeners >= 0 && (
            <div className="mt-1.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              <span className="text-xs text-muted-foreground">
                {nowPlaying.listeners} listening now
              </span>
            </div>
          )}
      </div>
    </div>
  );
}

// ─── Mobile Player Bar ─────────────────────────────────────────────────────────
function PlayerBar({
  station,
  isPlaying,
  isLoading,
  volume,
  nowPlaying,
  onPlayPause,
  onVolumeChange,
  notificationsSupported,
  notificationsEnabled,
  onToggleNotifications,
}: {
  station: (typeof STATIONS)[0] | null;
  isPlaying: boolean;
  isLoading: boolean;
  volume: number;
  nowPlaying: NowPlaying | null;
  onPlayPause: () => void;
  onVolumeChange: (v: number) => void;
  notificationsSupported: boolean;
  notificationsEnabled: boolean;
  onToggleNotifications: () => void;
}) {
  if (!station) {
    return (
      <div className="player-bar fixed bottom-0 left-0 right-0 z-50 h-16 flex items-center justify-center">
        <p className="text-muted-foreground text-sm">
          Select a station to start listening
        </p>
      </div>
    );
  }

  return (
    <div className="player-bar fixed bottom-0 left-0 right-0 z-50">
      {/* Mobile layout: stacked track info + controls */}
      <div className="flex flex-col">
        {/* Track info row */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-1">
          <div className="min-w-0 flex-1">
            {nowPlaying ? (
              <p
                className="font-semibold text-sm truncate"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {nowPlaying.artist
                  ? `${nowPlaying.artist} — ${nowPlaying.title}`
                  : nowPlaying.title}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground/50 italic">
                Loading track info...
              </p>
            )}
          </div>
          {/* Blinking studio LIVE sign next to the live song */}
          {nowPlaying && (
            <span
              className="studio-live-sign shrink-0"
              style={{ fontSize: "1rem" }}
            >
              <span className="studio-live-dot" />
              LIVE
            </span>
          )}
          {/* Play/Pause — always visible, large tap target */}
          <button
            onClick={onPlayPause}
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 shrink-0"
            style={{ background: "var(--pb-green)" }}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isLoading ? (
              <svg
                className="animate-spin w-5 h-5 text-black"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="30 70"
                />
              </svg>
            ) : isPlaying ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-black"
              >
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-black ml-0.5"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>

        {/* Controls row: waveform + volume */}
        <div className="flex items-center gap-3 px-4 pb-3">
          <Waveform playing={isPlaying && !isLoading} small />
          <div className="flex items-center gap-2 flex-1">
            <button
              onClick={() => onVolumeChange(volume === 0 ? 0.8 : 0)}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 shrink-0"
              aria-label="Toggle mute"
            >
              {volume === 0 ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
              )}
            </button>
            <div className="volume-slider-wrap flex-1 relative flex items-center">
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={e => onVolumeChange(parseFloat(e.target.value))}
                className="w-full"
                aria-label="Volume slider"
              />
            </div>
          </div>
          {/* Now-Playing notification toggle (issue #48) */}
          {notificationsSupported && (
            <button
              onClick={onToggleNotifications}
              className={`shrink-0 p-1 transition-colors ${
                notificationsEnabled
                  ? "text-cyan-400 hover:text-cyan-300"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label={
                notificationsEnabled
                  ? "Turn off Now-Playing notifications"
                  : "Turn on Now-Playing notifications"
              }
              title={
                notificationsEnabled
                  ? "Now-Playing alerts on"
                  : "Get Now-Playing alerts"
              }
            >
              {notificationsEnabled ? (
                <Bell size={16} />
              ) : (
                <BellOff size={16} />
              )}
            </button>
          )}
          {(() => {
            // Station sponsorships (issue #51): name + link come from the
            // shared sponsorship map, not a hardcoded URL. Stations without
            // a sponsorship show nothing here.
            const sponsorship = sponsorshipForStation(station.id);
            if (!sponsorship) return null;
            const className =
              "text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors whitespace-nowrap hidden sm:block";
            return sponsorship.url ? (
              <a
                href={sponsorship.url}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
              >
                Sponsored by {sponsorship.name}
              </a>
            ) : (
              <span className={className}>Sponsored by {sponsorship.name}</span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── Main Home Component ───────────────────────────────────────────────────────
export default function Home() {
  const [activeStation, setActiveStation] = useState<
    (typeof STATIONS)[0] | null
  >(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /** Populated by useKaliBroadcast once the stream routes through Web Audio. */
  const streamGraphRef = useRef<StreamGraph | null>(null);
  /**
   * Stream failover state (issue #52): whether the user currently wants
   * audio playing — if a stream fails mid-attempt we walk the station's
   * ordered endpoint chain instead of just toasting an error. The chain
   * position itself is derived from audio.src on each error event.
   */
  const wantPlayRef = useRef(false);
  const activeStationRef = useRef<(typeof STATIONS)[0] | null>(null);

  useEffect(() => {
    activeStationRef.current = activeStation;
  }, [activeStation]);

  useEffect(() => {
    document.title = `${SITE_NAME} — ${SITE_TAGLINE}`;
  }, []);
  // ── Active stats URL for tRPC polling (null = no polling) ──
  const [activeStatsUrl, setActiveStatsUrl] = useState<string | null>(
    STATIONS.find(s => s.live)?.statsUrl ?? null
  );

  // ── Server-side metadata fetch via tRPC (no CORS issues) ──
  const { data: metaData, isError: metaQueryFailed } =
    trpc.radio.nowPlaying.useQuery(
      { statsUrl: activeStatsUrl! },
      {
        enabled: !!activeStatsUrl,
        refetchInterval: 15000,
        refetchIntervalInBackground: false,
        // Fail fast so the CORS-proxy fallback can run when the API route or cast4 is unreachable
        retry: 1,
      }
    );

  // ── Podcast channel now-playing (issue #106): the podcast channel has
  // no Shoutcast stats endpoint, so derive the episode from the 24/7 loop
  // position while it is active. Music stations keep the tRPC metadata path.
  const isPodcastActive = activeStation?.kind === "podcast";
  useEffect(() => {
    if (!isPodcastActive) return;
    const updatePodcastNowPlaying = () => {
      const { episodeIndex } = podcastLoopPosition();
      const ep = PODCAST_EPISODES[episodeIndex];
      if (ep) {
        setNowPlaying({
          artist: ep.speakers,
          title: `Ep ${ep.n}: ${ep.word} (${ep.duration})`,
        });
      }
    };
    updatePodcastNowPlaying();
    const id = window.setInterval(updatePodcastNowPlaying, 15000);
    return () => window.clearInterval(id);
  }, [isPodcastActive]);

  // Sync tRPC metadata into local nowPlaying state (music stations only —
  // the podcast channel sets its own now-playing above).
  useEffect(() => {
    if (activeStation?.kind === "podcast") return;
    if (metaData && !("error" in metaData)) {
      // Source restarts leave SONGTITLE empty until the next track change —
      // show something meaningful instead of a blank line.
      setNowPlaying({
        artist: metaData.artist,
        title: metaData.title || "Live on air",
        listeners: metaData.listeners,
      });
    }
  }, [metaData, activeStation?.kind]);

  // If Vercel (or another host) cannot reach cast4, tRPC returns an error payload or fails outright.
  // Same-origin browsers are still CORS-blocked from cast4; fetch via a public CORS proxy as backup.
  // Skipped for the podcast channel: it has no stats URL, so activeStatsUrl
  // is null and this effect is a no-op (guard kept explicit for readability).
  useEffect(() => {
    if (!activeStatsUrl) return;
    if (activeStation?.kind === "podcast") return;
    if (metaData && !("error" in metaData)) return;

    const serverFailed = (metaData && "error" in metaData) || metaQueryFailed;
    if (!serverFailed) return;

    // A definitive HTTP 4xx from the source (e.g. station mount down → 404)
    // means public CORS proxies will 404 too — skip the noisy client fallback
    // and only retry when the server path itself was unreachable.
    const serverError =
      metaData && "error" in metaData ? metaData.error : undefined;
    if (serverError && /^HTTP 4\d\d/.test(serverError)) {
      return;
    }

    const ac = new AbortController();
    (async () => {
      try {
        const text = await fetchStatsXmlViaCorsProxy(activeStatsUrl, ac.signal);
        if (ac.signal.aborted || text == null) return;
        const parsed = parseShoutcastStatsXml(text);
        if (!("error" in parsed)) {
          setNowPlaying({
            artist: parsed.artist,
            title: parsed.title || "Live on air",
            listeners: parsed.listeners,
          });
        }
      } catch {
        /* ignore */
      }
    })();
    return () => ac.abort();
  }, [metaData, metaQueryFailed, activeStatsUrl]);

  const startMetadataPolling = useCallback((station: (typeof STATIONS)[0]) => {
    setActiveStatsUrl(station.statsUrl ?? null);
  }, []);

  const stopMetadataPolling = useCallback(() => {
    // Keep polling active — metadata is always useful
  }, []);

  useEffect(() => {
    // Init audio element
    const audio = new Audio();
    // CORS mode lets useKaliBroadcast route the stream through Web Audio
    // (all mounts send Access-Control-Allow-Origin: *) — must be set
    // before any src assignment.
    audio.crossOrigin = "anonymous";
    audio.volume = volume;
    audio.preload = "none";
    audioRef.current = audio;
    audio.addEventListener("playing", () => {
      setIsPlaying(true);
      setIsLoading(false);
    });
    audio.addEventListener("waiting", () => setIsLoading(true));
    audio.addEventListener("canplay", () => setIsLoading(false));
    audio.addEventListener("error", () => {
      // ── Stream failover (issue #52): walk the station's ordered endpoint
      // chain (cast4 primary → sibling Centova hosts) before giving up.
      const station = activeStationRef.current;
      if (station && wantPlayRef.current) {
        // Derive position from the src that actually failed, so duplicate or
        // out-of-band error events can't skip or repeat endpoints.
        const failedIdx = Math.max(0, station.streamUrls.indexOf(audio.src));
        const nextIdx = failedIdx + 1;
        const nextUrl = station.streamUrls[nextIdx];
        if (nextUrl) {
          audio.src = nextUrl;
          audio.load();
          setActiveStatsUrl(station.statsUrls[nextIdx] ?? null);
          void audio.play().catch(() => {
            /* next error event continues the chain */
          });
          return;
        }
      }
      const wasTryingToPlay = wantPlayRef.current;
      wantPlayRef.current = false;
      // Re-anchor to the primary endpoint so the next attempt starts fresh.
      // Guard: re-assigning the same src would restart the load and re-fire
      // error in a loop.
      if (station && audio.src !== station.streamUrls[0]) {
        audio.src = station.streamUrls[0];
      }
      setIsPlaying(false);
      setIsLoading(false);
      if (wasTryingToPlay) {
        toast.error("Stream unavailable", {
          description: "Could not connect to the stream. Please try again.",
        });
      }
    });
    audio.addEventListener("pause", () => setIsPlaying(false));

    // Auto-select the live station immediately after audio is ready
    // so the player bar shows paused and the play button works on first tap
    const liveStation = STATIONS.find(s => s.live);
    if (liveStation) {
      setActiveStation(liveStation);
      if (liveStation.streamUrl) {
        audio.src = liveStation.streamUrl;
        audio.load();
      }
      setActiveStatsUrl(liveStation.statsUrl ?? null);
    }

    return () => {
      audio.pause();
      audio.src = "";
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectStation = useCallback(
    (station: (typeof STATIONS)[0]) => {
      if (!audioRef.current || !station.streamUrl) return;
      // ── Podcast channel loop (issue #106): one MP3 ending advances to the
      // next episode. The 'ended' listener is re-registered per tune-in (see
      // startPodcastLoop) so stale closures never fire after switching away.
      const startPodcastLoop = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if ((audio as HTMLAudioElement & { __podcastLoop?: boolean }).__podcastLoop) return;
        (audio as HTMLAudioElement & { __podcastLoop?: boolean }).__podcastLoop = true;
        audio.addEventListener("ended", () => {
          const current = activeStationRef.current;
          if (current?.kind !== "podcast" || !wantPlayRef.current) return;
          const idx = Math.max(0, current.streamUrls.indexOf(audio.src));
          const nextUrl = current.streamUrls[(idx + 1) % current.streamUrls.length];
          const { offsetSeconds } = podcastLoopPosition();
          audio.src = nextUrl;
          audio.load();
          // Join-in-progress: when the 24/7 clock is already deep into the
          // next episode, skip ahead instead of replaying from 0:00. Seek
          // failures (short file, slow range response) fall back to 0:00.
          const skipTo = Math.min(offsetSeconds, 300);
          const trySeek = () => {
            try {
              if (Number.isFinite(audio.duration) && audio.duration > skipTo) {
                audio.currentTime = skipTo;
              }
            } catch {
              /* play from the top */
            }
          };
          audio.addEventListener("loadedmetadata", trySeek, { once: true });
          void audio.play().catch(() => {
            /* next error event continues the chain */
          });
        });
      };
      if (activeStation?.id === station.id) {
        if (isPlaying) {
          wantPlayRef.current = false;
          audioRef.current.pause();
          stopMetadataPolling();
        } else {
          wantPlayRef.current = true;
          setIsLoading(true);
          if (station.kind === "podcast") {
            // Resume where the 24/7 loop is NOW, not where pause happened.
            const { episodeIndex, offsetSeconds } = podcastLoopPosition();
            const url = station.streamUrls[episodeIndex] ?? station.streamUrl;
            if (audioRef.current.src !== url) {
              audioRef.current.src = url;
              audioRef.current.load();
            }
            startPodcastLoop();
            const skipTo = Math.min(offsetSeconds, 300);
            const trySeek = () => {
              try {
                if (
                  Number.isFinite(audioRef.current!.duration) &&
                  audioRef.current!.duration > skipTo
                ) {
                  audioRef.current!.currentTime = skipTo;
                }
              } catch {
                /* play from the top */
              }
            };
            audioRef.current.addEventListener("loadedmetadata", trySeek, {
              once: true,
            });
          }
          audioRef.current.play().catch(() => setIsLoading(false));
          startMetadataPolling(station);
        }
        return;
      }
      // Switching stations: start fresh from the station's primary endpoint.
      wantPlayRef.current = true;
      audioRef.current.pause();
      stopMetadataPolling();
      setNowPlaying(null);
      if (station.kind === "podcast") {
        // Join the 24/7 loop mid-episode wherever it currently is.
        const { episodeIndex, offsetSeconds } = podcastLoopPosition();
        audioRef.current.src =
          station.streamUrls[episodeIndex] ?? station.streamUrl;
        setActiveStation(station);
        setIsLoading(true);
        setIsPlaying(false);
        audioRef.current.load();
        startPodcastLoop();
        const skipTo = Math.min(offsetSeconds, 300);
        const trySeek = () => {
          try {
            if (
              Number.isFinite(audioRef.current!.duration) &&
              audioRef.current!.duration > skipTo
            ) {
              audioRef.current!.currentTime = skipTo;
            }
          } catch {
            /* play from the top */
          }
        };
        audioRef.current.addEventListener("loadedmetadata", trySeek, {
          once: true,
        });
        audioRef.current.play().catch(() => setIsLoading(false));
        startMetadataPolling(station);
      } else {
        audioRef.current.src = station.streamUrl;
        setActiveStation(station);
        setIsLoading(true);
        setIsPlaying(false);
        audioRef.current.load();
        audioRef.current.play().catch(() => setIsLoading(false));
        startMetadataPolling(station);
      }
      // Station changed — no toast, UI feedback is the player bar updating
    },
    [activeStation, isPlaying, startMetadataPolling, stopMetadataPolling]
  );

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current || !activeStation) return;
    if (isPlaying) {
      wantPlayRef.current = false;
      audioRef.current.pause();
      stopMetadataPolling();
    } else {
      wantPlayRef.current = true;
      setIsLoading(true);
      audioRef.current.play().catch(() => setIsLoading(false));
      startMetadataPolling(activeStation);
    }
  }, [isPlaying, activeStation, startMetadataPolling, stopMetadataPolling]);

  const handleVolumeChange = useCallback((v: number) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    // Once routed through Web Audio, the element's volume is disconnected —
    // user volume lives on the graph's masterGain.
    const graph = streamGraphRef.current;
    if (graph && graph.ctx.state !== "closed") {
      graph.userVolume = v;
      graph.masterGain.gain.setTargetAtTime(v, graph.ctx.currentTime, 0.05);
    }
  }, []);

  // Play a Kali voice intro every 3-5 songs while the stream is active;
  // also inserts the active station's sponsor sweeper once per tune-in
  // (per-station ad insertion, issue #51).
  useKaliBroadcast({
    isPlaying,
    nowPlayingTitle: nowPlaying?.title,
    streamAudioRef: audioRef,
    streamGraphRef,
    stationId: activeStation?.id ?? null,
    stationSweeper: sponsorshipForStation(activeStation?.id)?.audio ?? null,
  });

  // ── Now-Playing web notifications (issue #48) — opt-in via the bell in
  // the player bar; announces track changes on the active station while the
  // tab is in the background. ──
  const notifications = useNowPlayingNotifications({
    station: activeStation,
    nowPlaying,
    isPlaying,
  });

  const handleToggleNotifications = useCallback(async () => {
    if (notifications.enabled) {
      notifications.disable();
      return;
    }
    const granted = await notifications.enable();
    if (!granted) {
      toast.error("Notifications blocked", {
        description:
          "Allow notifications for this site in your browser settings to get Now-Playing alerts.",
      });
    }
  }, [notifications]);

  // ── Lock screen / Control Center integration (Media Session API) ──
  const playPauseRef = useRef(handlePlayPause);
  useEffect(() => {
    playPauseRef.current = handlePlayPause;
  }, [handlePlayPause]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: nowPlaying?.title || activeStation?.name || SITE_NAME,
      artist: nowPlaying?.artist || SITE_TAGLINE,
      album: activeStation?.name || SITE_NAME,
      artwork: [{ src: "/icon-512.png", sizes: "512x512", type: "image/png" }],
    });
  }, [nowPlaying, activeStation]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
  }, [isPlaying]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const toggle = () => playPauseRef.current();
    navigator.mediaSession.setActionHandler("play", toggle);
    navigator.mediaSession.setActionHandler("pause", toggle);
    return () => {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
    };
  }, []);

  // Player bar height: ~88px on mobile (stacked), ~72px on desktop
  const playerBarHeight = activeStation ? "pb-[88px]" : "pb-16";

  return (
    <div className={`min-h-screen bg-background ${playerBarHeight}`}>
      {/* ── Header ── */}
      <header className="border-b border-border/50 sticky top-0 z-40 bg-background/95 backdrop-blur-md">
        <div className="container flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center gap-2.5 text-foreground hover:opacity-90 transition-opacity"
          >
            <SiteLogo className="h-12 sm:h-14" />
            <div className="sr-only">
              <h1>{SITE_NAME}</h1>
              <p>{SITE_TAGLINE}</p>
            </div>
            <p className="text-[9px] text-muted-foreground tracking-widest uppercase hidden sm:block leading-none">
              {SITE_TAGLINE}
            </p>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            <a
              href="https://aycepickleball.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Shop
            </a>
            <Link
              href="/schedule"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Schedule
            </Link>
            <Link
              href="/submit-music"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Submit Music
            </Link>
            <Link
              href="/advertise"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Advertise
            </Link>
            <Link
              href="/contact"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Contact
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-white/5 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span
              className={`block w-5 h-0.5 bg-foreground transition-all duration-200 ${menuOpen ? "rotate-45 translate-y-2" : ""}`}
            />
            <span
              className={`block w-5 h-0.5 bg-foreground transition-all duration-200 ${menuOpen ? "opacity-0" : ""}`}
            />
            <span
              className={`block w-5 h-0.5 bg-foreground transition-all duration-200 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`}
            />
          </button>
        </div>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-border/50 bg-background/98">
            <div className="container py-3 flex flex-col gap-1">
              <a
                href="https://aycepickleball.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground py-2.5 px-3 rounded-lg hover:bg-white/5 hover:text-foreground transition-colors font-medium"
                onClick={() => setMenuOpen(false)}
              >
                🛒 Shop AYCEpickleball.com
              </a>
              <Link
                href="/schedule"
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground py-2.5 px-3 rounded-lg hover:bg-white/5 hover:text-foreground transition-colors font-medium"
              >
                📅 Schedule
              </Link>
              <Link
                href="/submit-music"
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground py-2.5 px-3 rounded-lg hover:bg-white/5 hover:text-foreground transition-colors font-medium"
              >
                🎵 Submit Music
              </Link>
              <Link
                href="/advertise"
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground py-2.5 px-3 rounded-lg hover:bg-white/5 hover:text-foreground transition-colors font-medium"
              >
                📣 Advertise
              </Link>
              <Link
                href="/contact"
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground py-2.5 px-3 rounded-lg hover:bg-white/5 hover:text-foreground transition-colors font-medium"
              >
                ✉️ Contact
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero Section — Desktop only ── */}
      <section
        className="relative overflow-hidden hidden md:block"
        style={{ height: "clamp(360px, 65svh, 620px)" }}
      >
        {/* Background video (poster fallback to hero still) */}
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/hero-loop.mp4"
          poster="/hero-poster.png"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/30 to-background" />
        {/* On desktop: fade right side for brand rep */}
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-background/20 to-transparent pointer-events-none hidden md:block" />
        <div className="absolute bottom-0 left-0 right-0 h-px court-line" />

        {/* ── Brand Rep (kali) — full head visible, anchored bottom-right ── */}
        <div
          className="absolute inset-y-0 right-0 flex items-end justify-end pointer-events-none"
          style={{ width: "clamp(180px, 48%, 480px)" }}
        >
          {/* Green glow behind her */}
          <div
            className="absolute bottom-0 right-4 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{
              background: "var(--pb-green)",
              width: "70%",
              height: "55%",
            }}
          />

          <img
            src="/kali-brand-rep.png"
            alt={`${SITE_NAME} host`}
            className="brand-rep-listening relative z-10 w-auto h-full object-contain object-bottom"
            style={{
              filter:
                "drop-shadow(0 0 24px rgba(125,200,50,0.2)) drop-shadow(0 8px 20px rgba(0,0,0,0.6))",
              maxHeight: "100%",
              maxWidth: "100%",
            }}
          />
        </div>

        {/* Hero content — left side */}
        <div className="relative z-10 container h-full flex flex-col justify-end pb-10 pt-8">
          <div style={{ maxWidth: "min(380px, 58%)" }}>
            <div className="flex items-center gap-2 mb-3">
              <LiveBadge />
              <span className="text-xs text-muted-foreground font-mono">
                {nowPlaying?.listeners != null
                  ? `${Math.max(9, nowPlaying.listeners)} online`
                  : "9 online"}
              </span>
            </div>
            <h2
              className="font-black uppercase leading-none mb-3"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                color: "var(--pb-green)",
                fontSize: "clamp(2.2rem, 8vw, 4.5rem)",
                lineHeight: 0.95,
              }}
            >
              Your Court.
              <br />
              <span className="text-foreground">Your Music.</span>
            </h2>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed hidden sm:block">
              Eight genre-themed radio stations plus the 24/7 Pickldiction
              Podcast channel, built for pickleball players.
            </p>
            <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
              <button
                onClick={() => handleSelectStation(STATIONS[0])}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-black transition-all active:scale-95"
                style={{
                  background: "var(--pb-green)",
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: "1rem",
                  letterSpacing: "0.05em",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
                TUNE IN NOW
              </button>
              <a
                href="https://aycepickleball.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                AYCEpickleball.com →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── kali Brand Rep — Mobile hero, above the CoverFlow ── */}
      <div
        className="md:hidden relative overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #0a0f0a 0%, transparent 100%)",
        }}
      >
        {/* Green glow behind her */}
        <div
          className="absolute bottom-0 right-0 rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{
            background: "var(--pb-green)",
            width: "60%",
            height: "60%",
            right: "5%",
          }}
        />
        {/* kali — full-bleed on mobile, decks span edge to edge */}
        <img
          src="/kali-brand-rep.png"
          alt={`kali — ${SITE_NAME} host`}
          className="relative z-10 block w-full h-auto"
          style={{
            filter:
              "drop-shadow(0 0 20px rgba(125,200,50,0.25)) drop-shadow(0 6px 16px rgba(0,0,0,0.7))",
          }}
        />
        {/* Left scrim so overlaid text stays readable on the image */}
        <div
          className="absolute inset-y-0 left-0 w-1/2 z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, rgba(5,9,5,0.7) 0%, rgba(5,9,5,0.3) 50%, transparent 100%)",
          }}
        />
        {/* kali intro text — overlaid on the image, near kali's shoulder */}
        <p
          className="absolute z-20 font-semibold text-gray-200 text-sm md:text-base"
          style={{
            top: "30%",
            left: "7%",
            maxWidth: "45%",
            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
            lineHeight: 1.3,
          }}
        >
          Hi, I&apos;m kali,
          <br />
          KPKL&apos;s DJAI.
        </p>
      </div>

      {/* ── CoverFlow — Mobile only (below kali hero) ── */}
      <section
        className="md:hidden"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, #0a0f0a 40%, #050905 100%)",
          marginTop: 0,
          paddingTop: "1rem",
          paddingBottom: "0.25rem",
        }}
      >
        {(() => {
          const coverItems: CoverFlowItem[] = STATIONS.map(s => ({
            id: s.id,
            image: s.image,
            title: s.name,
            subtitle: s.tagline,
            badge: s.genre,
            badgeColor: s.genreColor,
            frequency: s.frequency,
            live: s.live,
          }));
          return (
            <CoverFlow
              items={coverItems}
              activeId={activeStation?.id}
              onSelect={(_item, idx) => handleSelectStation(STATIONS[idx])}
            />
          );
        })()}
      </section>

      {/* ── Owners: Dominick DeCarlo, hypnotist and site owner, and kali ── */}
      <div className="relative overflow-hidden text-center px-6 pt-6 pb-2 md:py-10">
        <img
          src="/dominick-kali-duo.png"
          alt="Dominick DeCarlo, hypnotist and allyoucaneatpickleball.com and KPKLradio.com owner, and kali"
          className="mx-auto w-full max-w-md h-auto rounded-2xl"
          style={{
            filter:
              "drop-shadow(0 0 20px rgba(125,200,50,0.25)) drop-shadow(0 6px 16px rgba(0,0,0,0.7))",
          }}
        />
        <p className="mt-4 text-sm md:text-base font-semibold text-gray-200 leading-relaxed">
          Dominick DeCarlo, hypnotist and{" "}
          <span className="whitespace-nowrap">allyoucaneatpickleball.com</span>{" "}
          and <span className="whitespace-nowrap">KPKLradio.com</span> owner, and
          kali
        </p>
      </div>

      {/* ── Stations Section — Desktop only ── */}
      <section className="hidden md:block py-12">
        <div className="container mb-2 md:mb-5">
          <div className="flex items-end justify-between">
            <div>
              <h2
                className="text-2xl md:text-4xl font-black uppercase mb-0.5"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Radio Stations
              </h2>
              <p className="text-muted-foreground text-xs md:text-sm">
                8 stations + podcast · All live · 24/7 non-stop
              </p>
            </div>
          </div>
        </div>

        {/* ── Cover Flow Carousel — Desktop ── */}
        {(() => {
          const coverItems: CoverFlowItem[] = STATIONS.map(s => ({
            id: s.id,
            image: s.image,
            title: s.name,
            subtitle: s.tagline,
            badge: s.genre,
            badgeColor: s.genreColor,
            frequency: s.frequency,
            live: s.live,
          }));
          return (
            <CoverFlow
              items={coverItems}
              activeId={activeStation?.id}
              onSelect={(_item, idx) => handleSelectStation(STATIONS[idx])}
            />
          );
        })()}
      </section>

      {/* ── About / Stats Section ── */}
      <section className="border-t border-border/50">
        <div className="container py-4 md:py-14">
          <div className="court-line mb-4 md:mb-8" />
          <div>
            <h3
              className="text-2xl md:text-4xl font-black uppercase mb-3"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                color: "var(--pb-green)",
              }}
            >
              Built for the
              <br />
              Pickleball Community
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base mb-3">
              {SITE_NAME} is the first streaming network dedicated entirely to
              the fastest-growing sport in the world. Whether you're warming up,
              competing, or cooling down, we've got the soundtrack for every
              moment on the court.
            </p>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
              Our stations are curated by players, for players — with genres
              spanning every taste in the pickleball community.
            </p>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-border/50">
        <div className="container py-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2.5 text-foreground">
            <SiteLogo className="h-7" />
            <span className="text-sm text-muted-foreground">
              {SITE_NAME} — {SITE_TAGLINE}
            </span>
          </div>
          <div className="flex items-center gap-5 text-xs text-muted-foreground flex-wrap">
            <a
              href={SITE_URL}
              className="hover:text-foreground transition-colors"
            >
              {SITE_DOMAIN}
            </a>
            <a
              href="https://aycepickleball.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              AYCEpickleball.com
            </a>
            <Link
              href="/schedule"
              className="hover:text-foreground transition-colors"
            >
              Schedule
            </Link>
            <Link
              href="/advertise"
              className="hover:text-foreground transition-colors"
            >
              Advertise
            </Link>
            <Link
              href="/contact"
              className="hover:text-foreground transition-colors"
            >
              Contact
            </Link>
          </div>
          {/* Fix #10: Updated copyright year to 2026 */}
          <p className="text-xs text-muted-foreground/40">
            © 2026 {SITE_NAME}.
          </p>
          {/* Wind Down drop links — kali's songbyuniverse ad points here. */}
          <p className="text-xs text-muted-foreground mt-2">
            <a
              href="https://songbyuniverse.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              songbyuniverse.com
            </a>
            {" · "}
            <a
              href="https://www.dominickdecarlo.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              dominickdecarlo.com
            </a>
          </p>
        </div>
      </footer>

      {/* ── Sticky Player Bar ── */}
      <PlayerBar
        station={activeStation}
        isPlaying={isPlaying}
        isLoading={isLoading}
        volume={volume}
        nowPlaying={nowPlaying}
        onPlayPause={handlePlayPause}
        onVolumeChange={handleVolumeChange}
        notificationsSupported={notifications.supported}
        notificationsEnabled={notifications.enabled}
        onToggleNotifications={handleToggleNotifications}
      />
    </div>
  );
}
